<template>
  <v-container fluid>
    <page-bar title="Add / Update Assets" :subtitle="pageSubtitle" />

    <v-row justify="center" class="px-4">
      <v-col cols="12" lg="10" xl="8">
        <grid-system :columns="{ xs: 1, sm: 2, md: 4 }" :gap="16">
          <div v-for="action in actions" :key="action.id" :id="action.tourId"
            :class="{ 'disabled-card': !CanUserAccessAddUpdateAssets() }">
            <card-section dense :title="action.name" :icon="action.icon" :selected="action.toggled"
              @click="OnActionCardClick(action.id)" />
          </div>
        </grid-system>

        <div class="mt-4">
          <AddUpdateAssetFromTemplate v-if="selectedAction == 0" id="v-step-0" />
          <AssetCsvImportWizard 
            v-if="selectedAction == 1" 
            data-v-step="1" 
            @import-complete="onImportComplete"
            @has-changes="wizardHasChanges = $event"
            @wizard-cancelled="onWizardCancelled"
          />
          <AssetImageImport v-if="selectedAction == 2" data-v-step="2" />
          <AssetExport v-if="selectedAction == 3" />
        </div>

        <v-tour name="addUpdateAssetsTour" :steps="tourSteps" />
      </v-col>
    </v-row>

    <v-dialog v-model="showActionChangeDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="text-h6 px-4">
          <v-icon left color="warning">mdi-alert</v-icon>
          Discard changes?
        </v-card-title>
        <v-card-text>
          You have an import in progress. If you leave now, your changes will be lost and you'll need to start again.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelActionChange">
            Stay Here
          </v-btn>
          <v-btn color="error" text @click="confirmActionChange">
            Discard & Leave
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";

import AddUpdateAssetFromTemplate from "../views/addUpdateAssets/AddUpdateAssetFromTemplate.vue";
// import AssetCsvImport from "../views/addUpdateAssets/AssetCsvImport.vue";
import AssetCsvImportWizard from "../views/addUpdateAssets/AssetCsvImportWizard/index.vue";
import AssetExport from "../views/addUpdateAssets/AssetExport.vue";
import AssetImageImport from "../views/addUpdateAssets/AssetImageImport.vue";

import PageBar from "../shared/PageBar.vue";
import GridSystem from "../shared/GridSystem.vue";
import CardSection from "../cards/CardSection.vue";

import { mapGetters } from "vuex";

export default {
  name: "AddUpdateAssets",
  components: {
    AddUpdateAssetFromTemplate,
    // AssetCsvImport,
    AssetCsvImportWizard,
    AssetExport,
    AssetImageImport,
    PageBar,
    GridSystem,
    CardSection,
  },
  props: {},
  computed: {},
  mounted: function () {
    this.ResetSelectedAsset();

    let tourCookie = this.GetCookie("addupdatetourseen");

    if (tourCookie == "") {
      this.$tours["addUpdateAssetsTour"].start();
      this.SetCookie("addupdatetourseen", true, 365);
    }
    this.$nextTick(() => {
      this.AttachSubtitleLinkHandler();
    });
  },
  data: () => ({
    selectedAction: null,
    wizardHasChanges: false,
    showActionChangeDialog: false,
    pendingActionId: null,
    pendingRouteNext: null,
    actions: [
      {
        id: 0,
        name: "Add Asset From Template",
        icon: "mdi-format-float-none",
        toggled: false,
        tourId: "v-step-0",
      },
      {
        id: 1,
        name: "Import Assets",
        icon: "mdi-file-table",
        toggled: false,
        tourId: "v-step-1",
      },
      {
        id: 2,
        name: "Create/Update Assets From Images",
        icon: "mdi-image-multiple",
        toggled: false,
        tourId: "v-step-2",
      },
      {
        id: 3,
        name: "Export Assets",
        icon: "mdi-export",
        toggled: false,
      },

    ],
    tourSteps: [
      {
        target: "#v-step-0",
        content:
          "Adding an asset from a template will allow you to add a single asset, along with it's attribute details and resources.",
        params: {
          placement: "bottom",
        },
      },
      {
        target: "#v-step-1",
        content:
          "The csv import facility will allow you to bulk import your assets.",
        params: {
          placement: "bottom",
        },
      },
      {
        target: "#v-step-2",
        content:
          "The import assets from images facility will allow you to upload a group of resources and the shared attribute details, each image will create a brand new asset.",
        params: {
          placement: "bottom",
        },
      },
    ],
    pageSubtitle:
      'Here you can add / update assets within this section and other sections within AssetXP. If you don\'t understand these functions, please click <a href="#" id="retake-tour-link">here</a> to retake the tour.',
  }),
  beforeDestroy() {
    if (this._tourLinkEl && this._tourLinkHandler) {
      this._tourLinkEl.removeEventListener("click", this._tourLinkHandler);
    }
  },
  methods: {
    ...mapGetters("authentication", ["CanUserAccessAddUpdateAssets"]),
    ...mapActions("assetxpstate", ["ResetSelectedAsset"]),
    ResetTour() {
      this.$tours["addUpdateAssetsTour"].start();
    },
    AttachSubtitleLinkHandler() {
      const el = document.getElementById("retake-tour-link");
      if (!el) return;
      if (this._tourLinkEl && this._tourLinkHandler) {
        this._tourLinkEl.removeEventListener("click", this._tourLinkHandler);
      }
      this._tourLinkEl = el;
      this._tourLinkHandler = (e) => {
        e.preventDefault();
        this.ResetTour();
      };
      el.addEventListener("click", this._tourLinkHandler);
    },
    SetSelectedAction(actionId) {
      this.$tours["addUpdateAssetsTour"].stop();

      if (this.selectedAction == actionId) {
        this.selectedAction = null;
        this.actions[actionId].toggled = false;
      } else {
        if (this.selectedAction != null) {
          this.actions[this.selectedAction].toggled = false;
        }

        this.selectedAction = actionId;
        this.actions[actionId].toggled = true;
      }
    },
    OnActionCardClick(actionId) {
      if (!this.CanUserAccessAddUpdateAssets()) return;
      
      if (this.selectedAction === 1 && this.wizardHasChanges) {
        this.pendingActionId = actionId;
        this.showActionChangeDialog = true;
      } else {
        this.SetSelectedAction(actionId);
      }
    },
    
    confirmActionChange() {
      this.showActionChangeDialog = false;
      this.wizardHasChanges = false;
      
      if (this.pendingActionId === '__route_leave__') {
        if (this.pendingRouteNext) {
          this.pendingRouteNext();
          this.pendingRouteNext = null;
        }
      } else if (this.pendingActionId !== null) {
        this.SetSelectedAction(this.pendingActionId);
      }
      this.pendingActionId = null;
    },
    
    cancelActionChange() {
      this.showActionChangeDialog = false;
      if (this.pendingActionId === '__route_leave__' && this.pendingRouteNext) {
        this.pendingRouteNext(false);
        this.pendingRouteNext = null;
      }
      this.pendingActionId = null;
    },
    
    onWizardCancelled() {
      this.wizardHasChanges = false;
      this.selectedAction = null;
      this.actions[1].toggled = false;
    },
    SetCookie(cname, cvalue, exdays) {
      const d = new Date();
      d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
      let expires = "expires=" + d.toUTCString();
      document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    },
    GetCookie(cname) {
      let name = cname + "=";
      let ca = document.cookie.split(";");
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == " ") {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length);
        }
      }
      return "";
    },
    ImageUploadTriggered() {
      this.SetSelectedAction(2);
    },
    onImportComplete({ success, error }) {
      if (success) {
        console.log('Import completed successfully');
        this.wizardHasChanges = false;
      } else if (error) {
        console.error('Import failed:', error);
      }
    },
  },
  
  beforeRouteLeave(to, from, next) {
    if (this.selectedAction === 1 && this.wizardHasChanges) {
      this.pendingRouteNext = next;
      this.pendingActionId = '__route_leave__';
      this.showActionChangeDialog = true;
    } else {
      next();
    }
  }
};
</script>

<style>
.disabled-card {
  pointer-events: none;
  opacity: 0.6;
}
</style>
